import React from "react"
import Tooltip from "design_system/core/components/tooltip"
import { cn } from "@love-irish/core-components"

// The canonical level/XP progress shape (matches the GraphQL LevelProgressType + the codex
// serializer's `levelProgress`). Every field optional/nullable so callers can pass partial data.
export interface LevelProgressData {
  currentLevel?: number | string | null
  /** 0-100. */
  progressPercentage?: number | null
  /** Cumulative XP earned. */
  totalXp?: number | null
  /** XP within the current level (bar numerator). */
  xpIntoLevel?: number | null
  /** XP the current level spans (bar denominator). */
  xpForLevel?: number | null
  /** XP remaining to the next level. */
  xpToNextLevel?: number | null
}

const clampPct = (pct?: number | null) => Math.min(100, Math.max(0, pct ?? 0))

export interface LevelProgressBarProps {
  progress?: LevelProgressData | null
  className?: string
}

export const demoProgress = {
  currentLevel: "??",
  progressPercentage: 0.1,
  totalXp: 0,
  xpIntoLevel: 0,
  xpForLevel: 25,
}
/**
 * Compact inline progress: a small XP bar + "LVL n", with an XP tooltip. The standard replacement
 * for the old ProfileCurrentLevel — use it for any level (profile, skill, resource) by passing that
 * subject's `levelProgress`.
 */
export const LevelProgressBar: React.FC<LevelProgressBarProps> = ({
  progress,
  className,
}) => {
  if (!progress?.currentLevel) return null

  const pct = clampPct(progress.progressPercentage)

  return (
    <Tooltip
      title={`${progress.xpIntoLevel ?? 0}/${progress.xpForLevel ?? 0} EXP`}
    >
      <span
        className={cn(
          "font-black text-warning inline-flex gap-2 items-center",
          className
        )}
      >
        <span
          className="bg-warning-50 rounded overflow-hidden h-1.5 w-8 inline-flex border border-warning"
          role="progressbar"
          aria-valuemax={100}
          aria-valuemin={0}
          aria-valuenow={Math.round(pct)}
        >
          <span className="bg-warning" style={{ width: `${pct || 10}%` }}>
            &nbsp;
          </span>
        </span>
        LVL {progress.currentLevel}
      </span>
    </Tooltip>
  )
}

export interface LevelProgressCardProps {
  progress?: LevelProgressData | null
  /** Optional label above the level (e.g. "Codex level", or the record name). */
  label?: React.ReactNode
  className?: string
}

/**
 * Full progress display: a large level, a full-width XP bar, and the "X XP to level n+1" caption.
 * Used inside the codex / skill level cards. Renders nothing without a level.
 */
export const LevelProgressCard: React.FC<LevelProgressCardProps> = ({
  progress,
  label,
  className,
}) => {
  if (!progress?.currentLevel) return null

  const pct = clampPct(progress.progressPercentage)
  const level = progress.currentLevel

  return (
    <div className={className}>
      {label && <p className="text-xs font-bold text-secondary-600">{label}</p>}
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-2xl font-bold text-primary-600">Lvl {level}</span>
      </div>
      <div
        className="mt-2 h-2 w-full overflow-hidden rounded-full bg-secondary-100"
        role="progressbar"
        aria-valuemax={100}
        aria-valuemin={0}
        aria-valuenow={Math.round(pct)}
      >
        <div
          className="h-full rounded-full bg-primary-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      {progress.xpToNextLevel != null && progress.xpToNextLevel > 0 && (
        <p className="mt-1 text-xs text-secondary-600">
          {progress.xpToNextLevel} XP to level {level + 1}
        </p>
      )}
    </div>
  )
}

export default LevelProgressBar
